import React, { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import ClipLoader from "react-spinners/ClipLoader";
import { API } from "../../constants/api";
import useAuthAxios from "../../utils/baseAxios";
import { useUser } from "../../context/user-context";
import { darkNoTransaction, noTransaction } from "../../assets/images";
import { groupByDate } from "../../utils/groupByDate";
import type { ActivityItem } from "../../utils/activity/normalizeTransaction";
import ActivityRow from "./ActivityRow";

type Props = {
  items: ActivityItem[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage?: boolean;
  onEndReached: () => void;
  hideAmounts?: boolean;
  onSelect: (raw: any) => void;
  /** Trims the bottom padding reserved for the Activity page's fixed CTA bar. */
  dense?: boolean;
};

/** Gift card thumbnails come from the catalogue, fetched only when a row needs one. */
const useGiftCardImages = (enabled: boolean) => {
  const axiosInstance = useAuthAxios();
  // Same key and unwrapping as SelectCard.tsx so the two share one cache entry.
  const { data } = useQuery({
    queryKey: ["get-giftcards"],
    queryFn: async () => (await axiosInstance.get(API.getGiftCards)).data.data,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    const map: Record<string, string> = {};
    (data?.giftCards ?? []).forEach((card: any) => {
      if (card?._id) map[card._id] = card.thumbnail ?? "";
    });
    return map;
  }, [data]);
};

const ActivityList = ({
  items,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  onEndReached,
  hideAmounts,
  onSelect,
  dense,
}: Props) => {
  const { theme } = useUser();
  const hasGiftCards = items.some((i) => i.type === "giftcard");
  const giftCardImages = useGiftCardImages(hasGiftCards);
  const sections = useMemo(() => groupByDate(items), [items]);

  const { ref, inView } = useInView({ rootMargin: "200px" });

  useEffect(() => {
    // The guard is mandatory: without it the observer re-fires while a fetch is
    // already in flight and walks several cursors the user never scrolled to.
    // (In dev, StrictMode runs this effect twice — the guard covers that too.)
    if (inView && hasNextPage && !isFetchingNextPage) onEndReached();
  }, [inView, hasNextPage, isFetchingNextPage, onEndReached]);

  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center py-20">
        <ClipLoader color="#5E91FF" size={40} />
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="w-full flex flex-col justify-center items-center py-16">
        <img
          src={theme === "dark" ? darkNoTransaction : noTransaction}
          alt=""
          className="w-[120px] h-[120px]"
        />
        <h4 className="text-[13px] text-gray-500 dark:text-gray-400 mt-4">
          No transactions yet
        </h4>
      </div>
    );
  }

  return (
    <div className={`w-full ${dense ? "pb-2" : "pb-28"}`}>
      {sections.map((section) => (
        <div key={section.title} className="w-full mt-4">
          <h4 className="text-[12px] font-bold text-gray-500 dark:text-gray-400 mb-1">
            {section.title}
          </h4>
          <div className="w-full rounded-xl overflow-hidden">
            {section.data.map((item: ActivityItem) => (
              <ActivityRow
                key={item.id}
                item={item}
                giftCardImage={giftCardImages[item.iconMeta.value]}
                hideAmounts={hideAmounts}
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
      ))}

      <div ref={ref} className="h-4" />

      {isFetchingNextPage && (
        <div className="w-full flex justify-center py-4">
          <ClipLoader color="#5E91FF" size={20} />
        </div>
      )}
      {!hasNextPage && items.length > 0 && (
        <h4 className="text-[11px] text-center text-gray-400 py-4">
          No more transactions to load
        </h4>
      )}
    </div>
  );
};

export default React.memo(ActivityList);
